import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { X, FileText, UploadSimple, PaperPlaneTilt, Trash, Printer, Certificate } from "@phosphor-icons/react";
import { toast } from "sonner";

// Manage a worker's files: resume (upload/replace, view/print, email, delete)
// and certifications (OSHA cards etc.) with optional expiry dates.
export default function ResumeModal({ worker, onClose, onChanged }) {
  const [meta, setMeta] = useState(worker.resume || null);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const [certs, setCerts] = useState(worker.certs || []);
  const [certForm, setCertForm] = useState({ name: "", expires_on: "" });
  const [certBusy, setCertBusy] = useState(false);
  const certFileRef = useRef(null);

  const uploadCert = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("name", certForm.name.trim());
    if (certForm.expires_on) fd.append("expires_on", certForm.expires_on);
    setCertBusy(true);
    try {
      const { data } = await api.post(`/workers/${worker.id}/certs`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setCerts(data.certs || []);
      setCertForm({ name: "", expires_on: "" });
      toast.success("Certification added.");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setCertBusy(false); if (certFileRef.current) certFileRef.current.value = ""; }
  };

  const delCert = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/workers/${worker.id}/certs/${c.id}`);
      setCerts((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Certification deleted.");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
  };

  const pickCertFile = () => {
    if (!certForm.name.trim()) { toast.error("Enter a certification name first (e.g. OSHA 30)."); return; }
    certFileRef.current?.click();
  };

  const expiryBadge = (exp) => {
    if (!exp) return <span className="text-xs text-slate-400">no expiry</span>;
    const days = Math.floor((new Date(exp) - new Date()) / 86400000);
    if (days < 0) return <span className="text-xs font-bold text-rose-600">EXPIRED {exp}</span>;
    if (days <= 30) return <span className="text-xs font-bold text-amber-600">expires {exp} · {days}d left</span>;
    return <span className="text-xs text-slate-500">expires {exp}</span>;
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/workers/${worker.id}/resume`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const replaced = !!meta;
      setMeta(data.resume);
      toast.success(`Resume ${replaced ? "replaced" : "uploaded"} for ${worker.name}.`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async () => {
    if (!window.confirm(`Delete the resume on file for ${worker.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/workers/${worker.id}/resume`);
      setMeta(null);
      toast.success("Resume deleted.");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/workers/${worker.id}/resume/email`, { to: emailTo, note: note || null });
      toast.success(`Resume emailed to ${data.to} (copy BCC'd to billing@).`);
      setEmailOpen(false); setEmailTo(""); setNote("");
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setSending(false); }
  };

  const kb = meta ? Math.max(1, Math.round(meta.size / 1024)) : 0;
  const btn = "h-10 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-1.5 transition-colors";

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="resume-modal" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Files — {worker.name}</h2>
          <button type="button" onClick={onClose} data-testid="resume-modal-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
               data-testid="resume-upload-input" onChange={upload} />

        {meta ? (
          <>
            <div className="border border-slate-200 rounded-md p-3 flex items-center gap-3 bg-slate-50">
              <FileText size={28} weight="duotone" className="text-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm break-words" data-testid="resume-filename">{meta.filename}</div>
                <div className="text-xs text-slate-500">{kb} KB · uploaded {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <a href={`${API_BASE}/workers/${worker.id}/resume`} target="_blank" rel="noreferrer"
                 data-testid="resume-view-link"
                 className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>
                <Printer size={15} weight="bold" /> View / Print
              </a>
              <button onClick={() => setEmailOpen((v) => !v)} data-testid="resume-email-toggle"
                      className={`${btn} ${emailOpen ? "border-green-600 bg-green-50 text-green-800" : "border-green-300 text-green-700 hover:bg-green-50"}`}>
                <PaperPlaneTilt size={15} weight="bold" /> Email…
              </button>
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="resume-replace-btn"
                      className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50`}>
                <UploadSimple size={15} weight="bold" /> Replace
              </button>
              <button onClick={del} disabled={busy} data-testid="resume-delete-btn"
                      className={`${btn} border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50`}>
                <Trash size={15} weight="bold" /> Delete
              </button>
            </div>

            {emailOpen && (
              <form onSubmit={sendEmail} className="mt-4 border border-green-200 bg-green-50/60 rounded-md p-3">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Send to</label>
                <input required type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                       placeholder="recipient@company.com" data-testid="resume-email-to"
                       className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-3 block">Note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="e.g. Resume for the Marathon project, as requested."
                       data-testid="resume-email-note"
                       className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
                <div className="mt-3 flex justify-end">
                  <button disabled={sending} data-testid="resume-email-send"
                          className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md text-sm disabled:opacity-60">
                    {sending ? "Sending…" : "Send resume"}
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">Sent with the resume attached · reply-to and BCC to billing@safetyonsitesolutions.com</div>
              </form>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <FileText size={40} weight="duotone" className="text-slate-300 mx-auto" />
            <div className="mt-2 text-sm text-slate-500">No resume on file for {worker.name}.</div>
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="resume-upload-btn"
                    className="mt-4 h-11 px-5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md inline-flex items-center gap-2 disabled:opacity-60">
              <UploadSimple size={18} weight="bold" /> {busy ? "Uploading…" : "Upload resume"}
            </button>
            <div className="mt-2 text-xs text-slate-400">PDF or Word · max 10 MB</div>
          </div>
        )}

        <div className="mt-6 border-t border-slate-200 pt-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Certifications (OSHA cards, etc.)</div>
          {certs.length === 0 && <div className="text-sm text-slate-400 mb-2" data-testid="certs-empty">None on file.</div>}
          {certs.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 mb-2" data-testid={`cert-row-${c.id}`}>
              <Certificate size={18} weight="duotone" className="text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold break-words">{c.name}</div>
                {expiryBadge(c.expires_on)}
              </div>
              <a href={`${API_BASE}/workers/${worker.id}/certs/${c.id}`} target="_blank" rel="noreferrer"
                 data-testid={`cert-view-${c.id}`} className="text-green-600 font-semibold text-xs hover:underline">View</a>
              <button onClick={() => delCert(c)} data-testid={`cert-delete-${c.id}`}
                      className="text-slate-400 hover:text-rose-600"><Trash size={14} /></button>
            </div>
          ))}
          <input ref={certFileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden"
                 data-testid="cert-upload-input" onChange={uploadCert} />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <input value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })}
                   placeholder="Name — e.g. OSHA 30" data-testid="cert-name-input"
                   className="h-10 px-3 border border-slate-300 rounded-md text-sm" />
            <input type="date" value={certForm.expires_on} onChange={(e) => setCertForm({ ...certForm, expires_on: e.target.value })}
                   data-testid="cert-expiry-input" title="Expiry date (optional)"
                   className="h-10 px-3 border border-slate-300 rounded-md text-sm" />
          </div>
          <button onClick={pickCertFile} disabled={certBusy} data-testid="cert-add-btn"
                  className="mt-2 h-10 px-3 rounded-md border border-green-300 text-green-700 hover:bg-green-50 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <UploadSimple size={15} weight="bold" /> {certBusy ? "Uploading…" : "Add certification (choose file)"}
          </button>
          <div className="mt-1 text-[11px] text-slate-400">PDF, Word, or photo (JPG/PNG) · setting an expiry date turns on automatic reminders</div>
        </div>
      </div>
    </div>
  );
}
